import { Clock, Droplet, Pill, Scale, Target, type LucideIcon } from "lucide-react";
import type { LocalMedication } from "../services/MedicationService";
import medicationService from "../services/MedicationService";
import type { FingerPrickLogEntry } from "./fingerPrickHistory";
import {
  fingerLogEntriesToPastReadings,
  mergeFingerPrickPastReadings,
} from "./fingerPrickHistory";
import type { MeasurePageData, MeasurePredictionItem } from "../services/MeasureService";
import type { GlucoseFingerSnapshot, GlucoseReadingsUi } from "./glucoseReadingsModel";

export type InputAccent = "warning" | "default";

export type InputCalibrationKind = "finger-prick" | "eating-window" | "medication";

export type InputCalibrationItem = {
  id: string;
  kind?: InputCalibrationKind;
  /** For medication LOG NOW → `/food_scanner/medication/done/` */
  timingId?: string;
  medicationTaken?: boolean;
  title: string;
  value: string;
  subtitleCaps: string;
  Icon: LucideIcon;
  accent: InputAccent;
  leftAction?: string;
  rightAction?: string;
  showActions?: boolean;
};

export type AttentionItem = {
  id: string;
  title: string;
  subtitle: string;
  Icon: LucideIcon;
  /** Warning styling when meta indicates stale / recalibrate */
  stale: boolean;
};

export type FingerPrickPastReading = {
  /** Human label: CGM, Finger prick, Fasting glucose, HbA1c */
  typeLabel: string;
  value: string;
  /** Date/time line shown under the type badge */
  meta: string;
  /** Pre-meal or Post-meal when known (finger prick logs). */
  mealTimingLabel?: "Pre-meal" | "Post-meal";
  /** Newest-first ordering in history sheet. */
  sortKey?: number;
};

export const parseMealTimingLabel = (
  text: string,
): "Pre-meal" | "Post-meal" | undefined => {
  const t = text.toLowerCase().replace(/_/g, " ");
  if (t.includes("post-meal") || t.includes("post meal")) return "Post-meal";
  if (t.includes("pre-meal") || t.includes("pre meal")) return "Pre-meal";
  return undefined;
};

export const mealTimingLabelFromKey = (
  timing?: "pre-meal" | "post-meal" | "",
): "Pre-meal" | "Post-meal" | undefined => {
  if (timing === "pre-meal") return "Pre-meal";
  if (timing === "post-meal") return "Post-meal";
  return undefined;
};

export const sortPastReadingsNewestFirst = (
  readings: FingerPrickPastReading[],
): FingerPrickPastReading[] =>
  [...readings].sort((a, b) => (b.sortKey ?? 0) - (a.sortKey ?? 0));

const MONTHS_PARSE: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const formatHistoryMeta = (date: string, time: string): string => {
  const [yRaw, moRaw, dRaw] = date.split("-");
  const y = Number(yRaw);
  const mo = Number(moRaw);
  const d = Number(dRaw);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const dateLabel =
    y && mo && d ? `${months[mo - 1] ?? "???"} ${String(d).padStart(2, "0")}, ${y}` : date;
  const [hhRaw, mmRaw] = time.split(":");
  let h = Number.parseInt(hhRaw ?? "0", 10);
  const m = mmRaw ?? "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${dateLabel} · ${h}:${m} ${ampm}`;
};

const sortKeyFromDateTime = (date: string, time: string): number => {
  const ms = new Date(`${date}T${time || "00:00"}:00`).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const parseMetaSortKey = (meta: string): number | undefined => {
  const upper = meta.toUpperCase();
  const monthMatch = upper.match(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+(\d{1,2}),\s+(\d{4})/);
  if (monthMatch) {
    const mo = MONTHS_PARSE[monthMatch[1].toLowerCase()];
    const day = Number(monthMatch[2]);
    const year = Number(monthMatch[3]);
    const timeMatch = meta.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (mo != null && timeMatch) {
      let h = Number(timeMatch[1]);
      const m = timeMatch[2];
      const pm = timeMatch[3].toUpperCase() === "PM";
      if (pm && h < 12) h += 12;
      if (!pm && h === 12) h = 0;
      return sortKeyFromDateTime(
        `${year}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        `${String(h).padStart(2, "0")}:${m}`,
      );
    }
  }
  return undefined;
};

const fingerReadingFromSnapshot = (
  snap: GlucoseFingerSnapshot,
  mealTimingLabel: "Pre-meal" | "Post-meal",
): FingerPrickPastReading => ({
  typeLabel: "Finger prick",
  value: `${snap.value} mg/dL`,
  meta: formatHistoryMeta(snap.date, snap.time),
  mealTimingLabel,
  sortKey: sortKeyFromDateTime(snap.date, snap.time),
});

const buildFingerReadingsFromGlucoseUi = (
  glucose?: GlucoseReadingsUi | null,
): FingerPrickPastReading[] => {
  if (!glucose) return [];
  const rows: FingerPrickPastReading[] = [];

  if (glucose.fingerPrickPre?.value) {
    rows.push(fingerReadingFromSnapshot(glucose.fingerPrickPre, "Pre-meal"));
  }
  if (glucose.fingerPrickPost?.value) {
    rows.push(fingerReadingFromSnapshot(glucose.fingerPrickPost, "Post-meal"));
  }

  const timing = mealTimingLabelFromKey(glucose.fingerPrickMealTiming);
  if (timing && glucose.fingerPrick.trim()) {
    const value = glucose.fingerPrick.includes("mg")
      ? glucose.fingerPrick.trim()
      : `${glucose.fingerPrick.trim()} mg/dL`;
    const alreadyCovered = rows.some(
      (row) =>
        row.mealTimingLabel === timing &&
        row.value.replace(/\s/g, "") === value.replace(/\s/g, ""),
    );
    if (!alreadyCovered) {
      rows.push({
        typeLabel: "Finger prick",
        value,
        meta: formatHistoryMeta(glucose.fingerPrickDate, glucose.fingerPrickTime),
        mealTimingLabel: timing,
        sortKey: sortKeyFromDateTime(glucose.fingerPrickDate, glucose.fingerPrickTime),
      });
    }
  }

  return rows;
};

const mealContextToLabel = (
  ctx?: string,
): "Pre-meal" | "Post-meal" | undefined => {
  if (ctx === "pre_meal") return "Pre-meal";
  if (ctx === "post_meal") return "Post-meal";
  return undefined;
};

function isHbA1cReadingType(...parts: Array<string | undefined>): boolean {
  return parts.some((p) => p?.toLowerCase().includes("hba1c") ?? false);
}

function extractHbA1cPercent(rawValue?: string, meta?: string): string | null {
  const value = rawValue?.trim() ?? "";
  if (value.includes("%")) {
    const match = value.match(/([\d.]+)\s*%/);
    if (match) return `${match[1]}%`;
  }

  const metaMatch = meta?.match(/hba1c\s*([\d.]+)\s*%/i);
  if (metaMatch) return `${metaMatch[1]}%`;

  if (value && !value.toLowerCase().includes("mg")) {
    const numeric = Number.parseFloat(value.replace(/[^\d.]/g, ""));
    if (!Number.isNaN(numeric) && numeric >= 3 && numeric <= 20) {
      return `${numeric}%`;
    }
  }

  return null;
}

/** Listing display — HbA1c shows % only, never mg/dL. */
export function formatPastReadingDisplayValue(reading: {
  typeLabel: string;
  value: string;
  meta?: string;
}): string {
  if (isHbA1cReadingType(reading.typeLabel, reading.meta)) {
    return extractHbA1cPercent(reading.value, reading.meta) ?? "—";
  }

  const value = reading.value?.trim() || "—";
  if (value === "—") return value;
  if (value.toLowerCase().includes("mg") || value.includes("%")) return value;
  return `${value} mg/dL`;
}

const mapPoweringItemToPastReading = (item: MeasurePredictionItem): FingerPrickPastReading => {
  const meta = item.meta?.trim() || "—";
  const typeLabel = readingTypeLabelFromTitle(item.title);
  // Prefer explicit meal_context from API; fall back to text-based parsing
  const mealTimingLabel =
    mealContextToLabel(item.meal_context) ??
    parseMealTimingLabel(`${item.title} ${item.meta ?? ""}`);
  const reading = {
    typeLabel,
    value: item.value?.trim() || "—",
    meta,
    mealTimingLabel,
  };
  return {
    ...reading,
    value: formatPastReadingDisplayValue(reading),
    sortKey: parseMetaSortKey(meta),
  };
};

/** Map measure API powering titles to the four glucose reading labels. */
export const readingTypeLabelFromTitle = (title: string): string => {
  const t = title.toLowerCase();
  if (t.includes("cgm")) return "CGM";
  if (t.includes("hba1c")) return "HbA1c";
  if (t.includes("fasting")) return "Fasting glucose";
  if (t.includes("finger")) return "Finger prick";
  if (
    t.includes("glucose") &&
    (t.includes("pre") || t.includes("post") || t.includes("meal"))
  ) {
    return "Finger prick";
  }
  return title.trim() || "Reading";
};

const iconForPoweringItem = (item: MeasurePredictionItem): LucideIcon => {
  const t = item.title.toLowerCase();
  if (t.includes("finger")) return Droplet;
  if (t.includes("eating") || t.includes("window")) return Clock;
  if (t.includes("weight")) return Scale;
  return Target;
};

const displayTitleForPowering = (item: MeasurePredictionItem, stale: boolean): string => {
  if (item.title.toLowerCase().includes("finger") && stale) {
    return "Recalibrate finger prick";
  }
  return item.title;
};

const findPowering = (
  items: MeasurePredictionItem[],
  ...keywords: string[]
): MeasurePredictionItem | undefined =>
  items.find((item) =>
    keywords.some((k) => item.title.toLowerCase().includes(k.toLowerCase())),
  );

const isFingerPrickItem = (item: MeasurePredictionItem): boolean =>
  item.title.toLowerCase().includes("finger");

export const getFingerPrickPoweringItems = (
  powering: MeasurePredictionItem[],
): MeasurePredictionItem[] => powering.filter(isFingerPrickItem);

const formatFingerPastMeta = (item: MeasurePredictionItem): string => {
  const title = item.title.trim();
  const meta = item.meta?.trim();
  if (meta) {
    if (title.toLowerCase().includes("baseline")) {
      return `${title} · ${meta}`;
    }
    return meta;
  }
  return title || "Finger prick";
};

/** All finger prick rows from GET `/food_scanner/measure/` for the selected date */
export const buildFingerPrickPastReadingsForDate = (
  data: MeasurePageData | null | undefined,
  date: string,
): FingerPrickPastReading[] => {
  if (!data?.date_local || data.date_local !== date) return [];

  return getFingerPrickPoweringItems(getPoweringItems(data)).map((item) =>
    mapPoweringItemToPastReading(item),
  );
};

/** Measure API + locally saved readings (e.g. 6 mg/dL then 45 mg/dL same day) */
export const buildMergedFingerPrickPastReadings = (
  data: MeasurePageData | null | undefined,
  date: string,
  localEntries: FingerPrickLogEntry[],
): FingerPrickPastReading[] => {
  const fromApi = buildFingerPrickPastReadingsForDate(data, date);
  const fromLocal = fingerLogEntriesToPastReadings(
    localEntries.filter((entry) => entry.date === date),
  );
  return mergeFingerPrickPastReadings(fromApi, fromLocal);
};

/** True for any glucose reading powering item (finger prick, CGM, fasting, HbA1c).
 *  Excludes non-reading signals like eating window / weight. */
const isGlucoseReadingItem = (item: MeasurePredictionItem): boolean => {
  const t = item.title.toLowerCase();
  return (
    t.includes("finger") ||
    t.includes("cgm") ||
    t.includes("fasting") ||
    t.includes("hba1c") ||
    (t.includes("glucose") &&
      (t.includes("pre") || t.includes("post") || t.includes("meal")))
  );
};

/**
 * Full glucose reading history (finger prick, CGM, fasting, HbA1c) from the
 * measure API powering list, merged with locally-saved entries.
 * Shown in the Log-past sheet — each row carries an explicit typeLabel badge.
 */
export const buildReadingHistoryPastReadings = (
  data: MeasurePageData | null | undefined,
  _date: string,
  localEntries: FingerPrickLogEntry[],
): FingerPrickPastReading[] => {
  const fromApi = getPoweringItems(data)
    .filter(isGlucoseReadingItem)
    .map(mapPoweringItemToPastReading);
  const fromLocal = fingerLogEntriesToPastReadings(localEntries);
  return sortPastReadingsNewestFirst(
    mergeFingerPrickPastReadings(fromApi, fromLocal),
  );
};

/** History sheet — measure API + local logs + glucose-readings pre/post snapshots. */
export const buildMeasureHistoryReadings = (
  measureData: MeasurePageData | null | undefined,
  date: string,
  localEntries: FingerPrickLogEntry[],
  glucose?: GlucoseReadingsUi | null,
): FingerPrickPastReading[] => {
  let readings = buildReadingHistoryPastReadings(measureData, date, localEntries);
  readings = mergeFingerPrickPastReadings(readings, buildFingerReadingsFromGlucoseUi(glucose));
  return sortPastReadingsNewestFirst(readings);
};

/** Attach Pre/Post label to the latest finger-prick row from glucose-readings API. */
export const enrichPastReadingsWithLatestFingerTiming = (
  readings: FingerPrickPastReading[],
  fingerPrick?: string,
  mealTiming?: "pre-meal" | "post-meal" | "",
): FingerPrickPastReading[] => {
  const label = mealTimingLabelFromKey(mealTiming);
  if (!label || !fingerPrick?.trim()) return readings;

  const normalized = fingerPrick.includes("mg")
    ? fingerPrick.trim()
    : `${fingerPrick.trim()} mg/dL`;

  let applied = false;
  return readings.map((reading) => {
    if (
      applied ||
      reading.typeLabel !== "Finger prick" ||
      reading.mealTimingLabel ||
      reading.value.replace(/\s/g, "") !== normalized.replace(/\s/g, "")
    ) {
      return reading;
    }
    applied = true;
    return { ...reading, mealTimingLabel: label };
  });
};

/** e.g. "CALIBRATED 1 DAY AGO", "3 DAYS OLD" */
export const parseDaysFromMeta = (meta?: string): number | null => {
  if (!meta) return null;
  const lower = meta.toLowerCase();
  if (lower.includes("today")) return 0;
  const dayMatch = lower.match(/(\d+)\s*day/);
  if (dayMatch) return Number(dayMatch[1]);
  return null;
};

export const isStaleMeta = (meta?: string): boolean => {
  const days = parseDaysFromMeta(meta);
  if (days !== null && days >= 2) return true;
  const lower = meta?.toLowerCase() ?? "";
  return lower.includes("recalibrate") || lower.includes("needs attention");
};

/** Read-only: stale finger prick summary for Needs attention (actions live under Inputs). */
export const buildAttentionItems = (
  powering: MeasurePredictionItem[],
): AttentionItem[] =>
  powering
    .filter((item) => item.title.toLowerCase().includes("finger"))
    .filter((item) => isStaleMeta(item.meta))
    .map((item, idx) => ({
      id: `attention-${idx}-${item.title.replace(/\s+/g, "-").toLowerCase()}`,
      title: displayTitleForPowering(item, true),
      subtitle: [item.value, item.meta].filter(Boolean).join(" · ").toUpperCase(),
      Icon: iconForPoweringItem(item),
      stale: true,
    }));

const DEFAULT_FINGER_CALIBRATION: InputCalibrationItem = {
  id: "finger-prick",
  kind: "finger-prick",
  title: "Finger prick",
  value: "—",
  subtitleCaps: "NO READING YET",
  Icon: Droplet,
  accent: "default",
  leftAction: "LOG NOW",
  rightAction: "History",
};

const DEFAULT_EATING_WINDOW_CALIBRATION: InputCalibrationItem = {
  id: "eating-window",
  kind: "eating-window",
  title: "Eating window",
  value: "—",
  subtitleCaps: "NOT SET",
  Icon: Clock,
  accent: "default",
  showActions: false,
};

export const buildCalibrationItems = (
  powering: MeasurePredictionItem[],
): InputCalibrationItem[] => {
  const fingerItems = getFingerPrickPoweringItems(powering);
  const finger = fingerItems[fingerItems.length - 1] ?? findPowering(powering, "finger");
  const eating = findPowering(powering, "eating", "window");
  const items: InputCalibrationItem[] = [];

  // Always show finger prick — user can log even when API has no reading yet
  if (finger) {
    const stale = isStaleMeta(finger.meta);
    items.push({
      id: "finger-prick",
      kind: "finger-prick",
      title: "Finger prick",
      value: finger.value?.trim() || "—",
      subtitleCaps: (finger.meta ?? "NO READING YET").toUpperCase(),
      Icon: Droplet,
      accent: stale ? "warning" : "default",
      leftAction: "LOG NOW",
      rightAction: "History",
    });
  } else {
    items.push({ ...DEFAULT_FINGER_CALIBRATION });
  }

  if (eating) {
    items.push({
      id: "eating-window",
      kind: "eating-window",
      title: "Eating window",
      value: eating.value?.trim() || "—",
      subtitleCaps: (eating.meta ?? "NOT SET").toUpperCase(),
      Icon: Clock,
      accent: "default",
      showActions: false,
    });
  } else {
    items.push({ ...DEFAULT_EATING_WINDOW_CALIBRATION });
  }

  return items;
};

export const METFORMIN_CALIBRATION_PLACEHOLDER: InputCalibrationItem = {
  id: "metformin",
  kind: "medication",
  title: "Add Medication",
  value: "",
  subtitleCaps: "TAKEN 8:14 AM - ON SCHEDULE",
  Icon: Pill,
  accent: "default",
  leftAction: "LOG NOW",
  rightAction: "History",
};

export const buildWeightActivitySubtitle = (weight?: MeasurePredictionItem): string => {
  if (!weight?.meta) return weight?.value ? "" : "NOT LOGGED";
  return weight.meta.toUpperCase();
};

export const buildFamilyGroupSubtitle = (data: MeasurePageData | null | undefined): string => {
  const group = data?.group;
  if (!group) return "—";
  const name = (group.name ?? "Family").toUpperCase();
  const count = group.member_count ?? 0;
  const memberLabel = count === 1 ? "MEMBER" : "MEMBERS";
  return `${name} • ${count} ${memberLabel}`;
};

export const getPoweringItems = (data: MeasurePageData | null | undefined): MeasurePredictionItem[] =>
  data?.["Powering This Prediction"] ?? [];

export const getWeightPoweringItem = (
  powering: MeasurePredictionItem[],
): MeasurePredictionItem | undefined => findPowering(powering, "weight");

/** Build a single medication calibration card from the user's medication list.
 *  Uses the first pending (not_taken) medication, falling back to the first taken one,
 *  and finally to the static placeholder when no medications are available. */
export const buildSingleMedicationCalibrationCard = (
  medications: LocalMedication[],
): InputCalibrationItem => {
  const pending = medications.find((m) => m.status === "not_taken");
  const med = pending ?? medications[0];

  if (!med) return METFORMIN_CALIBRATION_PLACEHOLDER;

  const taken = med.status === "taken";
  const timeLabel = medicationService.formatTimeForDisplay(med.time ?? "");
  const subtitleCaps = taken
    ? `TAKEN ${timeLabel} - ON SCHEDULE`.toUpperCase()
    : timeLabel
    ? `DUE ${timeLabel} - NOT TAKEN YET`.toUpperCase()
    : "NOT TAKEN YET";

  return {
    id: `medication-${med.timing_id}`,
    kind: "medication",
    timingId: med.timing_id,
    medicationTaken: taken,
    title: "Add Medication",
    value: "",
    subtitleCaps,
    Icon: Pill,
    accent: taken ? "default" : "warning",
    leftAction: "LOG NOW",
    rightAction: "History",
  };
};
